import { api } from '@/lib/trpc/react';
import * as Sentry from '@sentry/nextjs';

const CHUNK_SIZE = 5 * 1024 * 1024;

export interface UploadState {
  uploaded: number;
  total: number;
  percentage: number;
  error: string | null;
}

export interface CompletedPart {
  ETag: string;
  PartNumber: number;
}

interface MultipartConfig {
  chatbotId: string;
  type: 'video' | 'audio' | 'recording' | 'attachment';
  onError?: (error: Error) => void;
  onProgress?: (state: UploadState) => void;
  createdAt?: string;
}

export const useMultipartUpload = () => {
  const { mutateAsync: initiateUpload } =
    api.aws.initiateMultipartUpload.useMutation();
  const { mutateAsync: completeUpload } =
    api.aws.completeMultipartUpload.useMutation();

  const uploadPart = async (
    chunk: Blob,
    url: string,
    partNumber: number,
    retries = 3,
  ): Promise<CompletedPart> => {
    try {
      const response = await fetch(url, {
        method: 'PUT',
        body: chunk,
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const eTag = response.headers.get('etag') || response.headers.get('ETag');
      if (!eTag) {
        throw new Error('No ETag received');
      }

      return {
        ETag: eTag.replace(/^"(.*)"$/, '$1'),
        PartNumber: partNumber,
      };
    } catch (err) {
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return uploadPart(chunk, url, partNumber, retries - 1);
      }
      throw err;
    }
  };

  const uploadFile = async (
    file: File,
    config: MultipartConfig,
  ): Promise<string> => {
    try {
      const initialState: UploadState = {
        uploaded: 0,
        total: file.size,
        percentage: 0,
        error: null,
      };

      config.onProgress?.(initialState);

      const chunks = Math.ceil(file.size / CHUNK_SIZE);
      const { uploadId: multipartUploadId, signedUrls } = await initiateUpload({
        chatbotId: config.chatbotId,
        contentLength: file.size,
        parts: chunks,
        version: 'v1',
        type: config.type,
        createdAt: config.createdAt,
      });

      const uploads = signedUrls.map(({ url, partNumber }) => {
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        return uploadPart(chunk, url, partNumber);
      });

      // Track progress of all uploads
      let completedSize = 0;
      const progressPromises = uploads.map(async (promise, index) => {
        await promise;
        completedSize += Math.min(CHUNK_SIZE, file.size - index * CHUNK_SIZE);
        config.onProgress?.({
          uploaded: completedSize,
          total: file.size,
          percentage: Math.round((completedSize / file.size) * 100),
          error: null,
        });
      });

      // Wait for all uploads and progress updates
      const [parts] = await Promise.all([
        Promise.all(uploads),
        Promise.all(progressPromises),
      ]);

      await completeUpload({
        chatbotId: config.chatbotId,
        uploadId: multipartUploadId,
        parts,
        type: config.type,
        createdAt: config.createdAt,
      });

      return config.chatbotId;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Upload failed');
      const errorMessage = error.message;

      config.onProgress?.({
        uploaded: 0,
        total: file.size,
        percentage: 0,
        error: errorMessage,
      });

      if (config.onError) {
        config.onError(error);
      }

      Sentry.captureException(err);
      throw error;
    }
  };

  return {
    uploadFile,
  };
};
